import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageContainer } from '../../../shared/components/PageContainer';
import { api } from '../../../shared/lib/axios';
import {
  useClientesParaVaga,
  useCriarRequisicao,
  useSalvarRascunho,
  RECRUTAMENTO_KEYS,
} from '../hooks';
import { formatClienteLabel } from '../components/format';
import type { CreateRequisitionPayload, HiringReason } from '../api';
import { RequisicaoForm, type RequisicaoFormOption } from '../components/RequisicaoForm';

interface EnvelopedList<T> {
  data?: T[];
}

/** Empresas e centros de custo vêm de master data já existente; clientes, do catálogo do módulo. */
function useOpcoes() {
  const companies = useQuery({
    queryKey: ['recrutamento', 'opcoes', 'empresas'],
    queryFn: async () => {
      const { data } = await api.get<EnvelopedList<Record<string, string>>>('/companies?isActive=true&limit=100');
      const rows = Array.isArray(data) ? data : (data.data ?? []);
      return rows.map<RequisicaoFormOption>((row) => ({
        id: row.id,
        label: row.tradeName ?? row.corporateName ?? row.id,
      }));
    },
    staleTime: 5 * 60 * 1000,
  });

  const costCenters = useQuery({
    queryKey: ['recrutamento', 'opcoes', 'centros-de-custo'],
    queryFn: async () => {
      const { data } = await api.get<EnvelopedList<Record<string, string>>>('/cost-centers');
      const rows = Array.isArray(data) ? data : (data.data ?? []);
      return rows.map<RequisicaoFormOption>((row) => ({
        id: row.id,
        label: row.code ? `${row.code} — ${row.name}` : row.name,
      }));
    },
    staleTime: 5 * 60 * 1000,
  });

  const hiringReasons = useQuery({
    queryKey: RECRUTAMENTO_KEYS.motivosContratacao(),
    queryFn: async () => {
      const { data } = await api.get<EnvelopedList<HiringReason>>('/recruitment/hiring-reasons');
      const rows = Array.isArray(data) ? data : (data.data ?? []);
      return rows as HiringReason[];
    },
    staleTime: 5 * 60 * 1000,
  });

  return { companies, costCenters, hiringReasons };
}

/** Abertura de requisição — envio direto ou salvamento como rascunho. */
export default function NovaRequisicaoPage(): React.ReactElement {
  const navigate = useNavigate();
  const { companies, costCenters, hiringReasons } = useOpcoes();
  const clientes = useClientesParaVaga();
  const criar = useCriarRequisicao();
  const rascunho = useSalvarRascunho();

  const erro =
    (criar.error as Error | null)?.message ?? (rascunho.error as Error | null)?.message ?? null;

  function irParaDetalhe(id: string) {
    navigate(`/recrutamento/requisicoes/${id}`);
  }

  return (
    <PageContainer
      title="Nova requisição de vaga"
      subtitle="A requisição segue para Gestor → RH → Financeiro → Diretoria → Recrutamento"
      breadcrumbs={[
        { label: 'Requisições', to: '/recrutamento/requisicoes' },
        { label: 'Nova' },
      ]}
    >
      <RequisicaoForm
        companies={companies.data ?? []}
        clients={(clientes.data ?? []).map((cliente) => ({
          id: cliente.id,
          label: formatClienteLabel(cliente),
        }))}
        costCenters={costCenters.data ?? []}
        hiringReasons={hiringReasons.data ?? []}
        loading={criar.isPending || rascunho.isPending}
        error={erro}
        onSubmit={(payload: CreateRequisitionPayload) =>
          criar.mutate(payload, { onSuccess: (created) => irParaDetalhe(created.id) })
        }
        onSaveDraft={(payload: CreateRequisitionPayload) =>
          rascunho.mutate(payload, { onSuccess: (created) => irParaDetalhe(created.id) })
        }
        onCancel={() => navigate('/recrutamento/requisicoes')}
      />
    </PageContainer>
  );
}
